import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../components/UI/ToastProvider';
import { Button } from '../../components/UI/Button';
import {
  adminTenantStorageService,
  StorageQuotaSettingsDto,
  TenantUserStorageUsageDto,
} from '../../services/adminTenantStorageService';
import { formatBytes } from '../../utils/formatBytes';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

function mbToBytes(mb: number): number {
  return Math.round(mb * 1024 * 1024);
}

function bytesToMb(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(2);
}

const TenantPrivateStoragePage: React.FC = () => {
  const { addToast } = useToast();
  const [quota, setQuota] = useState<StorageQuotaSettingsDto | null>(null);
  const [usageRows, setUsageRows] = useState<TenantUserStorageUsageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [perUserMb, setPerUserMb] = useState('');
  const [globalMb, setGlobalMb] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, list] = await Promise.all([
        adminTenantStorageService.getQuota(),
        adminTenantStorageService.listUserUsage(),
      ]);
      setQuota(q);
      setUsageRows(list);
      if (q) {
        setPerUserMb(bytesToMb(q.maxStoragePerUserBytes));
        setGlobalMb(q.globalMaxStorageBytes != null ? bytesToMb(q.globalMaxStorageBytes) : '');
      } else {
        setPerUserMb('512');
        setGlobalMb('');
      }
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Erro ao carregar dados de armazenamento.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveQuota = async () => {
    const n = Number(perUserMb.replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) {
      addToast('Informe um limite por usuário válido (MB).', 'error');
      return;
    }
    let globalBytes: number | null | undefined = undefined;
    if (globalMb.trim() !== '') {
      const g = Number(globalMb.replace(',', '.'));
      if (!Number.isFinite(g) || g <= 0) {
        addToast('Teto global inválido; deixe em branco para não usar.', 'error');
        return;
      }
      globalBytes = mbToBytes(g);
    } else {
      globalBytes = null;
    }
    setSaving(true);
    try {
      const dto = await adminTenantStorageService.upsertQuota({
        maxStoragePerUserBytes: mbToBytes(n),
        globalMaxStorageBytes: globalBytes,
      });
      setQuota(dto);
      addToast('Quota salva.', 'success');
      await load();
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Erro ao salvar quota.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Storage de usuários (drive privado)</h1>
        <p className="text-sm text-gray-600 mt-1">
          Configure limites por usuário e visualize consumo agregado. Nomes de arquivos privados não são listados aqui.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm max-w-xl">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quota do tenant</h2>
        {loading ? (
          <p className="text-gray-500">Carregando...</p>
        ) : (
          <>
            {!quota && (
              <p className="text-sm text-amber-700 mb-4">Nenhuma quota cadastrada — uploads privados ficam bloqueados até configurar.</p>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Limite por usuário (MB)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={perUserMb}
                  onChange={e => setPerUserMb(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teto global opcional (MB)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  value={globalMb}
                  onChange={e => setGlobalMb(e.target.value)}
                  placeholder="Em branco = sem teto global"
                />
              </div>
              <Button type="button" variant="primary" onClick={() => void saveQuota()} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar quota'}
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Consumo por usuário</h2>
          <Button type="button" variant="secondary" size="sm" onClick={() => void load()} disabled={loading}>
            Atualizar
          </Button>
        </div>
        {loading ? (
          <p className="p-6 text-gray-500">Carregando...</p>
        ) : usageRows.length === 0 ? (
          <p className="p-6 text-gray-500">Nenhum uso registrado no drive privado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Usuário</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">E-mail</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Arquivos</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Total</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">% limite</th>
                </tr>
              </thead>
              <tbody>
                {usageRows.map(row => (
                  <tr key={row.userId} className="border-t border-gray-100">
                    <td className="py-3 px-4 font-medium text-gray-900">{row.userName}</td>
                    <td className="py-3 px-4 text-gray-600">{row.email}</td>
                    <td className="py-3 px-4 text-right text-gray-600">{row.totalFiles}</td>
                    <td className="py-3 px-4 text-right text-gray-600">{formatBytes(row.totalSizeBytes)}</td>
                    <td className="py-3 px-4 text-right text-gray-600">
                      {Number(row.percentUsed).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantPrivateStoragePage;
